import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/shared/lib/cn';

export const RadioGroup = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(function RadioGroup({ className, ...props }, ref) {
  return <RadioGroupPrimitive.Root ref={ref} className={cn('grid gap-3', className)} {...props} />;
});

export const RadioGroupItem = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(function RadioGroupItem({ className, ...props }, ref) {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'border-input text-primary aspect-square size-5 shrink-0 rounded-full border-2 transition-colors outline-none',
        'data-[state=checked]:border-primary focus-visible:ring-ring/30 focus-visible:ring-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <Circle className="fill-primary size-2.5 stroke-none" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});

/**
 * A full-width, tappable card that behaves as a radio option. Used by the
 * onboarding flows where the choice deserves more than a 20px dot.
 */
export const RadioCard = React.forwardRef<
  React.ComponentRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
    icon?: React.ReactNode;
    title: string;
    description?: string;
  }
>(function RadioCard({ className, icon, title, description, ...props }, ref) {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'border-border bg-card group flex w-full items-center gap-3 rounded-xl border p-4 text-start transition-colors outline-none',
        'hover:border-primary/40 focus-visible:ring-ring/30 focus-visible:ring-2',
        'data-[state=checked]:border-primary data-[state=checked]:bg-accent/50',
        className,
      )}
      {...props}
    >
      {icon ? (
        <span className="bg-accent text-accent-foreground group-data-[state=checked]:bg-primary group-data-[state=checked]:text-primary-foreground flex size-11 shrink-0 items-center justify-center rounded-lg transition-colors [&_svg]:size-5">
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold">{title}</span>
        {description ? (
          <span className="text-muted-foreground mt-0.5 block text-xs leading-relaxed">
            {description}
          </span>
        ) : null}
      </span>
      <span className="border-input group-data-[state=checked]:border-primary flex size-5 shrink-0 items-center justify-center rounded-full border-2">
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <Circle className="fill-primary size-2.5 stroke-none" />
        </RadioGroupPrimitive.Indicator>
      </span>
    </RadioGroupPrimitive.Item>
  );
});
